import React, { useState } from 'react';
import { TableSkeleton } from '../../components/enterprise';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Paper, Button, useTheme,
    CircularProgress, List, ListItemButton, ListItemText,
    ListItemIcon, Fade, IconButton,
} from '@mui/material';
import {
    Dashboard as DashboardIcon,
    AdminPanelSettings as OperatorIcon,
    Business as OrgIcon,
    ArrowBack as BackIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';

const ContextChooser = () => {
    const navigate = useNavigate();
    const { user, switchOrg } = useAuth();
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const [view, setView] = useState('choose'); // 'choose' | 'select-org'
    const [organizations, setOrganizations] = useState([]);
    const [loadingOrgs, setLoadingOrgs] = useState(false);
    const [switching, setSwitching] = useState(null); // slug of org being switched to

    const fetchOrganizations = async () => {
        setLoadingOrgs(true);
        try {
            const response = await axios.get('/api/organizations');
            setOrganizations(response.data.filter(org => org.enabled));
        } catch (e) {
            console.error('Failed to fetch organizations:', e);
        } finally {
            setLoadingOrgs(false);
        }
    };

    const handleAccessPlatform = () => {
        setView('select-org');
        fetchOrganizations();
    };

    const handleSelectOrg = async (orgSlug) => {
        setSwitching(orgSlug);
        try {
            await switchOrg(orgSlug);
            navigate('/');
        } catch (e) {
            console.error('Failed to switch org:', e);
            setSwitching(null);
        }
    };

    const cardSx = (hoverColor) => ({
        width: 340,
        p: 4,
        bgcolor: 'background.paper',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        cursor: 'pointer',
        transition: 'all 0.2s',
        '&:hover': {
            borderColor: hoverColor,
            transform: 'translateY(-2px)',
            boxShadow: isDark
                ? '0 8px 24px rgba(0,0,0,0.4)'
                : '0 8px 24px rgba(0,0,0,0.1)',
        },
    });

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                bgcolor: 'background.default',
                px: 2,
            }}
        >
            <Box sx={{ textAlign: 'center', mb: 5 }}>
                <Box sx={{ mb: 3 }}>
                    <img
                        src={`${process.env.PUBLIC_URL}/assets/realops.png`}
                        alt="VerOps"
                        style={{ width: 160, height: 'auto' }}
                    />
                </Box>
                <Typography
                    variant="h4"
                    sx={{ color: 'text.primary', fontWeight: 700, mb: 1 }}
                >
                    Welcome, {user?.username}
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                    {view === 'choose'
                        ? 'Choose how you\'d like to access the platform'
                        : 'Select an organization to access'}
                </Typography>
            </Box>

            {view === 'choose' && (
                <Fade in>
                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: 'center' }}>
                        {/* Access Platform */}
                        <Paper elevation={0} sx={cardSx(theme.palette.primary.main)} onClick={handleAccessPlatform}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Box
                                    sx={{
                                        width: 64, height: 64, borderRadius: 2,
                                        bgcolor: isDark ? 'rgba(110, 168, 254, 0.1)' : 'rgba(9, 105, 218, 0.08)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        mx: 'auto', mb: 2,
                                    }}
                                >
                                    <DashboardIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                                </Box>
                                <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600, mb: 1 }}>
                                    Access Platform
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3, minHeight: 40 }}>
                                    Monitor applications, view dashboards, manage alerts and explore your observability data
                                </Typography>
                                <Button variant="outlined" fullWidth>
                                    Select Organization
                                </Button>
                            </Box>
                        </Paper>

                        {/* Access Operator Panel */}
                        <Paper elevation={0} sx={cardSx(theme.palette.success.main)} onClick={() => navigate('/operator/organizations')}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Box
                                    sx={{
                                        width: 64, height: 64, borderRadius: 2,
                                        bgcolor: isDark ? 'rgba(63, 185, 80, 0.1)' : 'rgba(26, 127, 55, 0.08)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        mx: 'auto', mb: 2,
                                    }}
                                >
                                    <OperatorIcon sx={{ fontSize: 32, color: 'success.main' }} />
                                </Box>
                                <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600, mb: 1 }}>
                                    Operator Panel
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3, minHeight: 40 }}>
                                    Manage organizations, configure platform settings, monitor tenants and system health
                                </Typography>
                                <Button variant="outlined" color="success" fullWidth>
                                    Open Operator Panel
                                </Button>
                            </Box>
                        </Paper>
                    </Box>
                </Fade>
            )}

            {view === 'select-org' && (
                <Fade in>
                    <Paper
                        elevation={0}
                        sx={{
                            width: '100%',
                            maxWidth: 480,
                            bgcolor: 'background.paper',
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 2,
                            overflow: 'hidden',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
                            <IconButton size="small" onClick={() => setView('choose')} sx={{ mr: 1 }}>
                                <BackIcon fontSize="small" />
                            </IconButton>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                Select Organization
                            </Typography>
                        </Box>

                        {loadingOrgs ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                                <TableSkeleton rows={4} columns={3} />
                            </Box>
                        ) : organizations.length === 0 ? (
                            <Box sx={{ py: 4, textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary">
                                    No organizations found
                                </Typography>
                            </Box>
                        ) : (
                            <List disablePadding>
                                {organizations.map((org) => (
                                    <ListItemButton
                                        key={org.slug}
                                        onClick={() => handleSelectOrg(org.slug)}
                                        disabled={switching !== null}
                                        sx={{
                                            py: 1.5,
                                            borderBottom: `1px solid ${theme.palette.divider}`,
                                            '&:last-child': { borderBottom: 'none' },
                                        }}
                                    >
                                        <ListItemIcon sx={{ minWidth: 40 }}>
                                            {switching === org.slug ? (
                                                <CircularProgress size={20} />
                                            ) : (
                                                <OrgIcon sx={{ color: 'primary.main' }} />
                                            )}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={org.name}
                                            secondary={org.slug}
                                            primaryTypographyProps={{ fontWeight: 500 }}
                                            secondaryTypographyProps={{ variant: 'caption' }}
                                        />
                                    </ListItemButton>
                                ))}
                            </List>
                        )}
                    </Paper>
                </Fade>
            )}

            <Typography variant="caption" sx={{ color: 'text.disabled', mt: 6 }}>
                VerOps Platform — Operator Access
            </Typography>
        </Box>
    );
};

export default ContextChooser;
